import {CrudStatus} from 'forms-crud';
import {FieldType, FieldRendererIntersectionProps} from 'forms';
import EditButton from './EditButton';
import EditorModal from './EditorModal';
import React, {Component, ReactNode} from 'react';
import {StatusOverlay} from 'forms-crud';

interface Props<
    Value extends any,
    E extends HTMLElement,
    P extends object
> {
    children: ReactNode;
    className?: string;
    field: FieldType<Value, E, P>;
    fieldProps: FieldRendererIntersectionProps<Value, E> & P;
    head?: ReactNode;
    isEditing: boolean;
    onSubmit?: (value: Value) => Promise<void>;
    onValueChange?: (value: Value) => void;
    submitOnChange?: boolean;
    value: Value;
}

interface State<Value extends any> {
    isEditorOpen: boolean;
    status: CrudStatus;
    value: Value;
}

export default class EditableContainer<
    Value extends any,
    E extends HTMLElement,
    P extends object,
> extends Component<Props<Value, E, P>, State<Value>> {

    public static defaultProps: Partial<Props<any, HTMLElement, {}>> = {
        onSubmit: () => Promise.resolve(),
        onValueChange: () => {},
    };

    public state: State<Value> = {
        isEditorOpen: false,
        status: 'ready',
        value: this.props.value,
    };

    protected handleCancel = () => {
        if (this.state.status === 'saving') {
            return;
        }
        this.setState({ isEditorOpen: false, value: this.props.value });
    }

    protected handleEdit = () => {
        this.setState({ isEditorOpen: true, status: 'ready' });
    }

    protected handleValueChange = async (value: Value) => {
        this.setState({ value });
        if (this.props.submitOnChange) {
            await this.handleSubmit(value);
        }
    }

    protected handleSubmit = async (value?: Value) => {
        this.setState({ status: 'saving' });
        try {
            await this.props.onSubmit(
                value !== undefined ? value : this.state.value
            );
            this.setState({ status: 'saved', isEditorOpen: false });
        } catch {
            this.setState({ status: 'error', value: this.props.value });
        }
    }

    public render() {
        const { state, props } = this;
        return <div className={`editable ${props.className}`}>
            {props.children}
            {props.isEditing && <>
                <EditButton onClick={this.handleEdit} />
                <EditorModal
                    disabled={state.status === 'saving'}
                    field={props.field}
                    fieldProps={props.fieldProps}
                    head={props.head}
                    isOpen={state.isEditorOpen}
                    onCancel={this.handleCancel}
                    onSubmit={this.handleSubmit}
                    onValueChange={this.handleValueChange}
                    submitOnChange={props.submitOnChange}
                    value={state.value}
                />
                <StatusOverlay status={state.status} />
            </>}
        </div>;
    }
}
